import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The salon's timezone. Availability is already computed in Melbourne
 * wall-clock everywhere (see convertUTCToMelbourne), so bookings must be
 * written in the same frame.
 *
 * Hard-coded to match the rest of the app. The day a shop opens in Perth or
 * Brisbane this becomes a per-store field — every use site reads this
 * constant so that change is one edit, not a hunt.
 */
export const STORE_TIMEZONE = 'Australia/Melbourne';

/**
 * The instant a client means when they pick a slot.
 *
 * Parsed in the STORE's timezone, never the browser's. Plain
 * `dayjs('2026-08-08 11:15 AM')` interprets the text in whatever timezone
 * the device is set to, so a visitor on UTC booked 11:15 AM and the salon
 * got 9:15 PM — the full ten-hour AEST offset — with nothing in the picker
 * to warn either of them, because availability was worked out in Melbourne
 * while the submission was not.
 */
export const storeDateTime = (date: string, time: string) =>
  dayjs.tz(`${date} ${time}`, 'YYYY-MM-DD h:mm A', STORE_TIMEZONE);

/**
 * An instant as the salon's wall clock reads it, with no zone attached: the
 * frame slots are in. `nowInStore()` is this for now; the booking window's
 * cutoff (G15) is this for now plus the salon's notice, so a clock change
 * between the two is counted as the API counts it, in real hours.
 */
export const wallClockInStore = (instant: string | number | Date) =>
  dayjs(dayjs(instant).tz(STORE_TIMEZONE).format('YYYY-MM-DDTHH:mm:ss'));

/**
 * Now, as the salon's wall clock reads it, with no zone attached.
 *
 * Availability compares naive Melbourne wall-clock values (slot labels, and
 * appointment times already converted out of UTC). Comparing those against a
 * device-local `dayjs()` mixes two frames, so a visitor abroad sees today's
 * slots opened or hidden by the wrong number of hours.
 */
export const nowInStore = () => wallClockInStore(Date.now());

/**
 * The salon's calendar day of an instant, as "2026-10-03": the day the
 * salon's own clock reads, wherever the visitor's device thinks it is.
 */
export const storeDay = (instant: string | number | Date) =>
  dayjs(instant).tz(STORE_TIMEZONE).format('YYYY-MM-DD');

/**
 * The salon's whole day as UTC instants, midnight to 23:59:59 on its own
 * clock, for asking the API for that day's bookings (GUARDRAILS G4). The API
 * returns the bookings that START inside the range, so a range that opens
 * late hides the morning's bookings and their groomers look free.
 */
export const storeDayRange = (day: string) => ({
  dateStart: dayjs.tz(`${day} 00:00:00`, 'YYYY-MM-DD HH:mm:ss', STORE_TIMEZONE).toISOString(),
  dateEnd: dayjs.tz(`${day} 23:59:59`, 'YYYY-MM-DD HH:mm:ss', STORE_TIMEZONE).toISOString(),
});
